import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth, getToken } from '../auth/AuthContext';
import { APIHOST } from '../../common';

interface EditRoomDialogProps {
  open: boolean;
  onClose: () => void;
  roomId: string;
  onSave: () => void;
}

interface RoomData {
  arrivalDate: string;
  departureDate: string;
  type?: string;
  holdStatus?: string;
  dailyRate?: number;
}

export default function EditRoomDialog({ open, onClose, roomId, onSave }: EditRoomDialogProps) {
  const { userToken } = useAuth();
  const [roomData, setRoomData] = useState<RoomData>({
    arrivalDate: '',
    departureDate: '',
    dailyRate: undefined
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && roomId) {
      fetchRoomData();
    }
  }, [open, roomId]);

  const fetchRoomData = async () => {
    try {
      setLoading(true);
      setError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/order-rooms/${roomId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch room data');
      }

      const data = await response.json();
      setRoomData({
        arrivalDate: data.data.arrivalDate || '',
        departureDate: data.data.departureDate || '',
        dailyRate: data.data.dailyRate || undefined
      });
    } catch (err) {
      console.error('Error fetching room data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch room data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/order-rooms/${roomId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            arrivalDate: roomData.arrivalDate,
            departureDate: roomData.departureDate,
            dailyRate: roomData.dailyRate || null
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update room');
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving room:', err);
      setError(err instanceof Error ? err.message : 'Failed to save room');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setRoomData({ arrivalDate: '', departureDate: '', dailyRate: undefined });
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        Edit Room
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={saving}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Arrival Date"
                type="date"
                value={roomData.arrivalDate}
                onChange={(e) => setRoomData(prev => ({ ...prev, arrivalDate: e.target.value }))}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                disabled={saving}
              />
              <TextField
                label="Departure Date"
                type="date"
                value={roomData.departureDate}
                onChange={(e) => setRoomData(prev => ({ ...prev, departureDate: e.target.value }))}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                disabled={saving}
              />
              <TextField
                label="Daily Rate"
                type="number"
                value={roomData.dailyRate || ''}
                onChange={(e) => setRoomData(prev => ({ 
                  ...prev, 
                  dailyRate: e.target.value ? parseFloat(e.target.value) : undefined 
                }))}
                InputProps={{
                  startAdornment: '$'
                }}
                placeholder="Enter daily rate"
                fullWidth
                disabled={saving}
                helperText="Leave empty if rate should not be changed"
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || saving || !roomData.arrivalDate || !roomData.departureDate}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}